import { FC, useCallback, useMemo } from "react";

import { convertStringToDate, formattedDateRange, formattedYearRange } from "src/js/util/dates";
import { TenureTypes } from "../../typings/Tenure";

import { FilterDateRange } from "./FilterDates";
import { FilterFloorArea } from "./FilterFloorArea";
import { FilterPropertyTypes, HiddenFieldPath } from "./FilterPropertyTypes";
import { FilterTenure } from "./FilterTenure";
import { FilterBedrooms } from "./FilterBedrooms";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { isZoopla } from "../../util/isZoopla";
import { HomeTrackComparablesFilterOptions } from "../../typings/Comparables";
import { DisabledMessage, FiltersProps } from "./Filters";
import { FilterOnMarket } from "./FilterOnMarket";
import { possibleHometrackCombinations } from "../../util/PropertyTypes";
import { DCSCondition } from "react-migration/lib/typings/comparables/dcs";
import { HometrackDCSCondition, HometrackTenureShorthandGQL } from "react-migration/lib/typings/comparables/Hometrack";
import { TenureShorthandGQL } from "react-migration/lib/typings/comparables/Comparables";

export type HometrackFilterFields =
  | "dateFrom"
  | "dateTo"
  | "dcsConditions"
  | `dcsConditions.${HiddenFieldPath}`
  | "tenure"
  | "floorArea"
  | "bedrooms"
  | "yearBuiltFrom"
  | "yearBuiltTo"
  | "onMarketOnly";

export const HometrackFilters: FC<FiltersProps<HometrackFilterFields, HomeTrackComparablesFilterOptions>> = ({
  disabled,
  disabledFields,
  priceMode,
  filters,
  updateFilters,
  selectedDCSConditions,
  unitPreference,
  theme,
}) => {
  const { t } = useTranslation();

  const startDate = filters.dateFrom === null ? null : convertStringToDate(filters.dateFrom);
  const setStartDate = (date?: Date | null) => updateFilters({ dateFrom: date ? formattedDateRange(date) : null });

  const endDate = filters.dateTo === null ? null : convertStringToDate(filters.dateTo);
  const setEndDate = (date?: Date | null) => updateFilters({ dateTo: date ? formattedDateRange(date) : null });

  const yearBuiltFrom = filters.yearBuiltFrom ? convertStringToDate(filters.yearBuiltFrom.toString()) : null;
  const setYearBuiltFrom = (date?: Date | null) =>
    updateFilters({ yearBuiltFrom: date ? formattedYearRange(date) : undefined });

  const yearBuiltTo = filters.yearBuiltTo ? convertStringToDate(filters.yearBuiltTo.toString()) : null;
  const setYearBuiltTo = (date?: Date | null) =>
    updateFilters({ yearBuiltTo: date ? formattedYearRange(date) : undefined });

  const setDCSConditions = useCallback(
    (dcsConditions: DCSCondition[]) => updateFilters({ dcsConditions: dcsConditions as HometrackDCSCondition[] }),
    [updateFilters]
  );
  const setTenureTypes = useCallback(
    (tenureArray: Array<TenureShorthandGQL | "Any">) =>
      updateFilters({ tenure: tenureArray as HometrackTenureShorthandGQL[] }),
    [updateFilters]
  );

  const setAreaMin = useCallback(
    (min: number) => {
      updateFilters({
        floorArea: {
          min: isNaN(Number(min)) || Number(min) === 0 ? undefined : (min as number),
          max: filters.floorArea?.max,
        },
      });
    },
    [filters, updateFilters]
  );
  const setAreaMax = useCallback(
    (max: number) => {
      updateFilters({
        floorArea: {
          min: filters.floorArea?.min,
          max: isNaN(Number(max)) ? undefined : (max as number),
        },
      });
    },
    [filters, updateFilters]
  );
  const setBedroomsMin = useCallback(
    (min: number) => {
      updateFilters({
        bedrooms: {
          min: isNaN(min) ? undefined : min,
          max: filters.bedrooms?.max,
        },
      });
    },
    [filters, updateFilters]
  );

  const setBedroomsMax = useCallback(
    (max: number) => {
      updateFilters({
        bedrooms: {
          min: filters.bedrooms?.min,
          max: isNaN(max) ? undefined : max,
        },
      });
    },
    [filters, updateFilters]
  );

  const setShowOnMarketOnly = useCallback((onMarketOnly: boolean) => updateFilters({ onMarketOnly }), [updateFilters]);

  return (
    <div className="flex flex-col text-xs md:text-sm gap-3">
      <DisabledMessage enableTooltip={disabledFields?.has("dcsConditions")}>
        <FilterPropertyTypes
          options={possibleHometrackCombinations}
          disabledFields={
            new Set(
              [...(disabledFields ?? [])]
                .filter((a) => /\.(Commercial|Residential|Other)+.*/.test(a))
                .map((a) => a.replace(/^dcsConditions\./, "") as HiddenFieldPath)
            )
          }
          disabled={disabled || disabledFields?.has("dcsConditions")}
          selectedPropTypes={selectedDCSConditions}
          setSelectedPropTypes={setDCSConditions}
          theme={theme}
        />
      </DisabledMessage>

      <DisabledMessage enableTooltip={disabledFields?.has("tenure")}>
        <FilterTenure
          disabled={disabled || disabledFields?.has("tenure")}
          tenureTypes={filters.tenure}
          setTenureTypes={setTenureTypes}
          availableTenures={[TenureTypes.FREEHOLD, TenureTypes.LEASEHOLD, TenureTypes.SHARED]}
        />
      </DisabledMessage>

      <DisabledMessage enableTooltip={disabledFields?.has("floorArea")}>
        <FilterFloorArea
          disabled={disabled || disabledFields?.has("floorArea")}
          setAreaMin={setAreaMin}
          areaMin={filters.floorArea?.min}
          areaMax={filters.floorArea?.max}
          setAreaMax={setAreaMax}
          unitPreference={unitPreference}
          theme={theme}
        />
      </DisabledMessage>

      <DisabledMessage
        description={t("comparables.map.sidebar.filters.bedroom_filter_disabled_for_epc")}
        enableTooltip={disabledFields?.has("bedrooms")}
      >
        <FilterBedrooms
          disabled={disabled || disabledFields?.has("bedrooms")}
          min={
            disabled || disabledFields?.has("bedrooms") || filters.bedrooms?.min == null ? NaN : filters.bedrooms.min
          }
          setMin={setBedroomsMin}
          max={
            disabled || disabledFields?.has("bedrooms") || filters.bedrooms?.max == null ? NaN : filters.bedrooms.max
          }
          setMax={setBedroomsMax}
          theme={theme}
        />
      </DisabledMessage>

      <DisabledMessage enableTooltip={disabledFields?.has("dateFrom") && disabledFields?.has("dateTo")}>
        <FilterDateRange
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          disabled={disabled || (disabledFields?.has("dateFrom") && disabledFields?.has("dateTo"))}
          label={
            isZoopla(priceMode)
              ? t("comparables.map.sidebar.filters.filters.zoopla_date")
              : t("comparables.map.sidebar.filters.filters.date")
          }
        />
      </DisabledMessage>

      <DisabledMessage
        description={t("comparables.map.sidebar.filters.built_filter_disabled_for_epc")}
        enableTooltip={disabledFields?.has("yearBuiltFrom") || disabledFields?.has("yearBuiltTo")}
      >
        <FilterDateRange
          startDate={yearBuiltFrom}
          setStartDate={setYearBuiltFrom}
          endDate={yearBuiltTo}
          setEndDate={setYearBuiltTo}
          disabled={disabled || (disabledFields?.has("yearBuiltFrom") && disabledFields?.has("yearBuiltTo"))}
          label={t("comparables.map.sidebar.filters.filters.built")}
          mode="year"
        />
      </DisabledMessage>

      {/* This data currently isn't available */}
      {/* <DisabledMessage enableTooltip={disabledFields?.has("onMarketOnly")}>
        <FilterOnMarket
          disabled={disabled || disabledFields?.has("onMarketOnly")}
          showOnMarketOnly={!!filters.onMarketOnly}
          setShowOnMarketOnly={setShowOnMarketOnly}
          theme={theme}
        />
      </DisabledMessage> */}
    </div>
  );
};

HometrackFilters.displayName = "HometrackFilters";
